import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import useCompanyMessaging from '../../hooks/useCompanyMessaging';
import { getJobTracking, reissueJobTrackingLink, revokeJobTrackingLink } from '../../services/tracking';
import ConfirmationModal from '../modals/confirmation';
import CustomerMessagePrompt from './customerMessagePrompt';
import { toast } from '../../utils/toast';

/**
 * Customer tracking link on the job (item 3.7): "The office can revoke it
 * early or issue a new one." Reads with `peek`, so opening a job never creates
 * a link or undoes a revoke — a link only appears at Confirmed, or when the
 * office presses "Issue new link" here.
 *
 * Office roles only; the caller gates on isOfficeUser().
 */

const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const stateOf = (link, t) => {
    if (link?.url) {
        const bits = [t('trackingLink.issued', { date: fmt(link.issuedAt) })];
        bits.push(link.expiresAt ? t('trackingLink.expires', { date: fmt(link.expiresAt) }) : t('trackingLink.openUntilCompleted'));
        bits.push(link.lastViewedAt ? t('trackingLink.lastOpened', { date: fmt(link.lastViewedAt) }) : t('trackingLink.notOpened'));
        return { ok: true, label: t('trackingLink.active'), meta: bits.join(' · ') };
    }
    if (link?.revokedAt) return { ok: false, label: t('trackingLink.revoked'), meta: t('trackingLink.revokedMeta', { date: fmt(link.revokedAt) }) };
    if (link?.expiresAt && new Date(link.expiresAt) <= new Date()) return { ok: false, label: t('trackingLink.expired'), meta: t('trackingLink.expiredMeta', { date: fmt(link.expiresAt) }) };
    return { ok: false, label: t('trackingLink.notIssued'), meta: t('trackingLink.issuedAutomatically') };
};

const TrackingLinkControl = ({ jobId, client, stage, onSendTracking }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { trackingEnabled, isLoaded } = useCompanyMessaging();
    const [busy, setBusy] = useState('');
    const [confirm, setConfirm] = useState(null);
    const [sending, setSending] = useState(false);

    const key = ['job-tracking-peek', jobId];
    const { data } = useQuery(key, () => getJobTracking(jobId, { peek: true }), {
        enabled: !!jobId && isLoaded && trackingEnabled,
        staleTime: 0,
        refetchOnWindowFocus: true,
    });
    if (!isLoaded || !trackingEnabled || !jobId) return null;

    const link = data?.code === 200 ? data.message : null;
    const status = stateOf(link, t);

    const run = async (kind) => {
        setBusy(kind);
        try {
            const response = kind === 'revoke' ? await revokeJobTrackingLink(jobId) : await reissueJobTrackingLink(jobId);
            if (response?.code !== 200) throw new Error(typeof response?.message === 'string' ? response.message : t('trackingLink.couldNotBeChanged'));
            toast.success(kind === 'revoke' ? t('trackingLink.revokedToast') : t('trackingLink.reissuedToast'));
            queryClient.invalidateQueries(key);
            // The prepared-message panel caches its own read of the link.
            queryClient.removeQueries(['job-tracking', jobId]);
        } catch (error) {
            toast.error(error?.message || t('trackingLink.couldNotBeChanged'));
        } finally {
            setBusy('');
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link.url);
            toast.success(t('trackingLink.copied'));
        } catch {
            toast.error(t('trackingLink.copyNotAvailable'));
        }
    };

    return (
        <div className="jdoc-row">
            <div className="jdoc-info">
                <div className="jdoc-title">
                    {t('trackingLink.title')}
                    <span className={`jdoc-pill${status.ok ? ' ok' : ''}`}>{status.label}</span>
                </div>
                <div className="jdoc-status">{status.meta}</div>
            </div>
            <div className="jdoc-acts">
                <button type="button" className="jdoc-icon" disabled={!link?.url} title={t('trackingLink.sendToCustomer')} aria-label={t('invoiceShare.send')}
                    onClick={() => onSendTracking ? onSendTracking() : setSending(true)}>
                    <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                </button>
            </div>
            <div className="jdoc-sub">
                {link?.url && (
                    <button type="button" className="jdoc-chip" disabled={!!busy} onClick={copy}>
                        <i className="fa-solid fa-copy" aria-hidden="true"></i> {t('trackingLink.copyLink')}
                    </button>
                )}
                <button type="button" className="jdoc-chip" disabled={!!busy} onClick={() => (link?.url ? setConfirm('reissue') : run('reissue'))}>
                    <i className={`fa-solid ${busy === 'reissue' ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i> {t('trackingLink.issueNew')}
                </button>
                {link?.url && (
                    <button type="button" className="jdoc-chip" disabled={!!busy} onClick={() => setConfirm('revoke')}>
                        <i className={`fa-solid ${busy === 'revoke' ? 'fa-spinner fa-spin' : 'fa-ban'}`} aria-hidden="true"></i> {t('trackingLink.revoke')}
                    </button>
                )}
            </div>
            <ConfirmationModal display={!!confirm} toggle={() => setConfirm(null)}
                title={confirm === 'revoke' ? t('trackingLink.confirmRevoke') : t('trackingLink.confirmReissue')}
                confirmText={confirm === 'revoke' ? t('trackingLink.revoke') : t('trackingLink.issueNew')} cancelText={t('common.cancel')}
                message={confirm === 'revoke'
                    ? t('trackingLink.revokeMessage')
                    : t('trackingLink.reissueMessage')}
                confirmAction={() => run(confirm)} />
            {sending && !onSendTracking && <CustomerMessagePrompt jobId={jobId} stage={stage} client={client} onClose={() => setSending(false)} />}
        </div>
    );
};

export default TrackingLinkControl;
